import { FC, ReactElement, useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  AgendaItemType,
  AttendanceData,
  AttendanceOption,
  canGivePowerOfAttorney,
  createDiagramDataForNotice,
  createTabs,
  DiagramData,
  getAgendaItems,
  GetNoticeForPowerOfAttorneyRecipientResponse,
  GetNoticeForShareholderResponse,
  GivePowerOfAttorneyModel,
  hasVoted,
  isProceduralMatter,
  MatterForVotingData,
  meetingUrl,
  meetingUrlState,
  nextAgendaItem,
  NoticeActiveView,
  NoticeForShareholderController,
  PoAOtherRecipientModel,
  PoARecipientRole,
  PowerOfAttorneyRecipientController,
  PowerOfAttorneyShareholderOption,
  prepareApproveAgendaVotingData,
  prepareAttendanceData,
  prepareMatterForVotingData,
  prepareMeetingChairVotingData,
  prevAgendaItem,
  ReceivedPowerOfAttorney,
  relativeVotes,
  RequestPageReload,
  ShareOwnership,
  votesForOwnership,
  VotingContext,
} from "./generalMeetingDomain";
import { TMessage } from "@/i18n/tt";
import { BottomSpacer, Box, Card, CardProps, VBox } from "@/components-v2/Box";
import { RadioButton, RadioButtonGroup } from "@/components-v2/RadioButton";
import { MTextInput } from "@/components-v2/TextInput";
import { Button, IconButton, LinkButton, ToggleButton } from "@/components-v2/Button";
import { isFailure, isOk, isPending, Result, VotingEligibilityOfShares } from "@/common/common";
import { Alert, AlertContainer } from "@/components-v2/Alert";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { useAppBar } from "@/appBar/useAppBar";
import { Tabs } from "@/components-v2/Tabs";
import {
  Attachments,
  AttachmentsCard,
  DownloadAsPdfButtonProps,
  HeaderMeetingInfo,
  LogoWithCompanyName,
  MatterExtraText,
  MatterText,
  MeetingDocumentView,
  NoticeActionsView,
} from "@/common/commonComponents";
import { AppBarData, CompanyOption } from "@/appBar/appBarDomain";
import { ThemeToggleButton, UserMenu } from "@/components-v2/AppBar";
import { formatDate, formatDateTime } from "@/i18n/dateFormat";
import { isMobileView } from "@/utils/screen";
import { Table } from "@/components-v2/Table";
import { percentOf } from "@/utils/numbers";
import { openDashboardForAccessType, PreviewState } from "@/meetings/meetingsDomain";
import {
  GeneralMeetingIndividualVote,
  getDecisionActionLabel,
  isInformationMatterInDocument,
} from "@/dbot/common/commonDomain";
import { IndividualVote } from "@/dbot/common/commonGeneralMeetingDomain";
import { GeneralMeetingStatus, getMeetingStatusTag } from "./electronicGeneralMeetingDomain";
import { when } from "@/utils/functions";
import { Autocomplete } from "@/components-v2/Autocomplete";
import { Markdown } from "@/text/Markdown";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { FilePreviewDialog } from "@/components-v2/FilePreview";
import AlertMessageStore, { AlertMessage } from "@/stores/AlertMessageStore";
import { EmptyState } from "@/components-v2/EmptyState";
import { Icon } from "@/components-v2/Icon";
import { Checkbox } from "@/components-v2/Checkbox";
import { Dialog } from "@/components-v2/Dialog";
import { DocumentType } from "@/common/documents";
import {
  useDispatch,
  useLanguage,
  useLocaleFormat,
  useMessageStore,
  useRootStore,
  useTranslation,
} from "@/stores/RootStoreContext";
import { DecisionType } from "@/common/decisions";
import {
  AgendaItemCard,
  AgendaItemCardProps,
  AgendaItemHeader,
} from "@/meetings/meetingsComponents";
import { OptionFieldModel } from "@/common/commonForms";
import { InfoButton } from "@/components-v2/InfoButton";
import { findShareTypeName, ShareType } from "@/common/share";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { compact, noop } from "lodash";
import { PieChart } from "@/components-v2/Sparkline";
import { AutoSizer } from "@/components-v2/AutoSizer";

const ReceivedPowerOfAttorneyWarning: FC<{
  receivedPowerOfAttorneys: ReceivedPowerOfAttorney[];
}> = observer(({ receivedPowerOfAttorneys }) => {
  const { t } = useTranslation();

  switch (receivedPowerOfAttorneys.length) {
    case 0:
      return null;
    case 1:
      return (
        <Alert
          type={"info"}
          message={t("poa.received.warning.singular", { name: receivedPowerOfAttorneys[0].name })}
        />
      );
    default:
      return (
        <Alert
          type={"info"}
          message={t("poa.received.warning.plural", { number: receivedPowerOfAttorneys.length })}
        />
      );
  }
});

const PowerOfAttorneyText: FC<{
  model: GivePowerOfAttorneyModel;
  ownership: ShareOwnership[];
  companyName: string;
  date: string;
  shareTypes: ShareType[];
}> = observer(({ model, ownership, companyName, date, shareTypes }) => {
  const { t, lang } = useTranslation();
  const locale = useLanguage();
  return (
    <>
      <Box justifyContent="center">
        <h3>{t("noticeForShareholder.poa")}</h3>
      </Box>
      {ownership.length === 0 && (
        <div>
          {t("noticeForShareholder.intro", {
            poaRecipientName: model.poaRecipientName,
            companyName: companyName,
            date: formatDateTime(locale, date),
          })}
        </div>
      )}
      {ownership.length === 1 && (
        <div>
          {t("noticeForShareholder.intro.singleshare-class", {
            poaRecipientName: model.poaRecipientName,
            companyName: companyName,
            number: ownership[0].numberOfShares,
            date: formatDateTime(locale, date),
          })}
        </div>
      )}
      {ownership.length > 1 && (
        <div>
          {t("noticeForShareholder.intro.multipleshares", {
            poaRecipientName: model.poaRecipientName,
            companyName: companyName,
            date: formatDateTime(locale, date),
          })}
          <VBox paddingTop>
            {ownership.map((e, i) => {
              const shareType = shareTypes.find((st) => st.sharetypeId === e.shareTypeId);
              return (
                <div key={e.shareTypeCode}>
                  ({alphabet[i]}) {e.numberOfShares}{" "}
                  {shareType ? findShareTypeName(shareType, lang) : ""}
                </div>
              );
            })}
          </VBox>
        </div>
      )}
      <div>{t("noticeForShareholder.bottom")}</div>
      {model.isShareholderCompany ? (
        <VBox>
          <div>{t("on-behalf-of", { name: model.shareholderName })}</div>
          <div>{model.contactPersonName}</div>
        </VBox>
      ) : (
        <VBox>
          <div>{model.shareholderName}</div>
        </VBox>
      )}
    </>
  );
});

const GivePowerOfAttorneyToOther: FC<{
  model: PoAOtherRecipientModel;
  companyName: string;
  isDisabled?: boolean;
}> = observer(({ model, companyName, isDisabled }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <MTextInput
        name={"noticeForShareholderOtherNamedRecipient"}
        label={t("noticeForShareholder.otherNamedRecipient.label")}
        model={model.name}
        disabled={isDisabled}
        autofocus
      />
      <MTextInput
        name={"noticeForShareholderOtherEmailRecipient"}
        label={t("noticeForShareholder.otherNamedRecipient.emailLabel")}
        model={model.email}
        disabled={isDisabled}
      />
      {model.suggestions.length === 1 && (
        <Alert
          message={t("noticeForShareholder.otherNamedRecipient.suggestion.single", {
            shareholder: model.suggestions[0].representative
              ? model.suggestions[0].name + " (" + model.suggestions[0].representative + ")"
              : model.suggestions[0].name,
          })}
          type={"info"}
        />
      )}
      {model.suggestions.length > 1 && (
        <>
          <Alert
            message={t("noticeForShareholder.otherNamedRecipient.suggestion.plural", {
              companyName: companyName,
            })}
            type={"info"}
          />
          <div>{t("noticeForShareholder.otherNamedRecipient.suggestion.plural.title")}</div>
          <RadioButtonGroup<PowerOfAttorneyShareholderOption>
            options={model.suggestions}
            idFn={(item) => item.id.toString()}
            labelFn={(item) =>
              item.representative ? `${item.name} (${item.representative})` : item.name
            }
            onChange={(_, item) => model.setSelectedSuggestion(item)}
            selected={model.selectedSuggestion}
          />
        </>
      )}
    </VBox>
  );
});

export const GivePowerOfAttorneyToRole: FC<{
  model: GivePowerOfAttorneyModel;
  isDisabled?: boolean;
}> = observer(({ model, isDisabled }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      {model.poaRecipientRoleModel.options.map((option) => (
        <VBox key={model.poaRecipientRoleModel.getId(option)}>
          <RadioButton
            name={"poaRecipientRole"}
            disabled={model.poaRecipientRoleModel.isDisabled(option) || isDisabled}
            label={t(model.poaRecipientRoleModel.getLabel(option))}
            description={model.poaRecipientRoleModel.getDescription(option)}
            onChange={(event) => {
              if (event.target.checked) {
                model.poaRecipientRoleModel.setValue(option);
              }
            }}
            checked={
              model.poaRecipientRoleModel.getId(model.poaRecipientRoleModel.value) ===
              model.poaRecipientRoleModel.getId(option)
            }
          />
          {model.poaRecipientRoleModel.value === PoARecipientRole.shareholder &&
            option === PoARecipientRole.shareholder && (
              <VBox>
                <Autocomplete
                  placeholder={t("search-or-select")}
                  items={model.shareholderOptions}
                  value={model.shareholderRecipientModel}
                  onSelect={(item) => (model.shareholderRecipientModel = item)}
                  onClear={() => (model.shareholderRecipientModel = undefined)}
                  idFn={(item) => String(item.id)}
                  labelFn={(item) =>
                    item.representative ? `${item.name} (${item.representative})` : item.name
                  }
                  disabled={isDisabled}
                />
              </VBox>
            )}
          {model.poaRecipientRoleModel.value === PoARecipientRole.other &&
            option === PoARecipientRole.other && (
              <GivePowerOfAttorneyToOther
                model={model.poaOtherRecipientModel}
                companyName={model.companyName}
                isDisabled={isDisabled}
              />
            )}
        </VBox>
      ))}
    </VBox>
  );
});

const alphabet = "abcdefghijklmnopqrstuvwxyz";

const GivePowerOfAttorneyView: FC<{
  model: GivePowerOfAttorneyModel;
  ownership: ShareOwnership[];
  companyName: string;
  date: string;
  receivedPowerOfAttorneys: ReceivedPowerOfAttorney[];
  attendanceData: AttendanceData;
  meetingStatus?: GeneralMeetingStatus;
  messageStore: AlertMessageStore;
  shareTypes: ShareType[];
}> = observer(
  ({
    model,
    ownership,
    companyName,
    date,
    receivedPowerOfAttorneys,
    attendanceData,
    meetingStatus,
    messageStore,
    shareTypes,
  }) => {
    const { t } = useTranslation();

    useEffect(() => {
      if (isOk(model.givePoAResult)) {
        messageStore.showSuccess(model.givePoAResult.data, { autoHide: true });
      } else if (isFailure(model.givePoAResult)) {
        messageStore.showError(model.givePoAResult.error, { autoHide: true });
      }
    }, [messageStore, model.givePoAResult]);

    return (
      <>
        <Card padding={"lg"} gap={"lg"}>
          <ReceivedPowerOfAttorneyWarning receivedPowerOfAttorneys={receivedPowerOfAttorneys} />
          <PowerOfAttorneyText
            model={model}
            ownership={ownership}
            companyName={companyName}
            date={date}
            shareTypes={shareTypes}
          />
          {!attendanceData.isFinished &&
            !attendanceData.isPresent &&
            meetingStatus !== GeneralMeetingStatus.FINISHED && (
              <>
                {!model.givenPowerOfAttorney && (
                  <VBox gap={"md"}>
                    <strong>{t("noticeForShareholder.poa.giveTo")}</strong>
                    <GivePowerOfAttorneyToRole model={model} />
                  </VBox>
                )}
                {model.poaRecipientRoleModel.value === PoARecipientRole.other &&
                  model.poaOtherRecipientModel.suggestions.length === 0 && (
                    <small>
                      <Markdown
                        value={t("noticeForShareholder.otherNamedRecipient.tips")}
                        enableLinks
                      />
                    </small>
                  )}
                <Box justifyContent={"space-between"}>
                  {!model.givenPowerOfAttorney && (
                    <Button
                      variant={"primary"}
                      label={t("noticeForShareholder.givePoAButton")}
                      disabled={model.isSendPoADisabled}
                      onClick={model.sendPoA}
                      loading={isPending(model.givePoAResult)}
                    />
                  )}
                  {model.givenPowerOfAttorney && (
                    <Button
                      variant={"secondary"}
                      icon={"arrow-counter-clockwise"}
                      color="error"
                      label={t("poa_revoke")}
                      onClick={model.revokePoA}
                      disabled={model.isAdminPreview}
                    />
                  )}
                </Box>
              </>
            )}
          <PoAFooter />
        </Card>
      </>
    );
  },
);

const PoAFooter = () => {
  const { t } = useTranslation();
  return (
    <>
      <br />
      <br />
      <Box>
        <small>
          <i>{t("noticeForShareholder.footer")}</i>
        </small>
      </Box>
    </>
  );
};

const ShareholderUserMenu: FC<{
  appBar: AppBarData | null;
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
}> = observer(({ appBar, data }) => {
  const link = openDashboardForAccessType(data.accessType, data.orgNo ?? "", data.shareholderId);
  const { t } = useTranslation();
  const history = useRootStore().history;
  const user: AppBarData["user"] =
    "noticeForShareholderId" in data && data.isCompany
      ? {
          name: data.contactPersonName ? data.contactPersonName : data.recipientName,
          company: data.contactPersonName ? data.recipientName : undefined,
          initials: data.contactInitials || data.recipientInitials,
          email: data.recipientEmail,
        }
      : { name: data.recipientName, initials: data.recipientInitials, email: data.recipientEmail };
  return appBar ? (
    <Box alignItems={"center"} overflow={"hidden"}>
      {!isMobileView() && (
        <VBox gap={false} overflow={"hidden"} textAlign={"right"}>
          <span className={"truncate"}>{data.recipientName}</span>
          {data.recipientEmail && <small className="subtle truncate">{data.recipientEmail}</small>}
        </VBox>
      )}
      <ThemeToggleButton />
      <UserMenu
        languages={appBar?.languages ?? []}
        user={user}
        portfolioUrl={"https://dcompany.no"}
        companies={[]}
        selectedCompany={appBar?.selectedCompany}
        actions={appBar?.actions ?? []}
        userMainAction={
          link ? (
            <Button
              label={t(link.label)}
              variant="secondary"
              size="sm"
              icon="arrow-square-out"
              onClick={() => history.push(link.url)}
            />
          ) : undefined
        }
        disableUserContextInMenu={
          data.shareholderId === undefined && data.poaRecipientRole === PoARecipientRole.other
        }
      />
    </Box>
  ) : null;
});

const Header: FC<{
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
  activeView: NoticeActiveView;
  isShareholder?: boolean;
  isRecipient?: boolean;
  givenPowerOfAttorneyTo?: string;
  backUrl?: string;
  edit?: () => void;
  isAdminPreview?: boolean;
  givePowerOfAttorney?: () => void;
  alertStore: AlertMessageStore;
}> = observer(
  ({
    data,
    isShareholder = true,
    isRecipient = false,
    givenPowerOfAttorneyTo,
    givePowerOfAttorney,
    activeView,
    backUrl,
    edit,
    isAdminPreview = false,
    alertStore,
  }) => {
    const history = useRootStore().history;
    const selectedCompany: CompanyOption = {
      organisationNumber: data.orgNo ?? "",
      name: data.companyName ?? "",
      accessType: data.accessType,
    };
    let appBar = useAppBar({ withoutMenu: true, withoutUserSettings: true });
    if (appBar !== null) {
      appBar = { ...appBar, selectedCompany: selectedCompany };
    }

    const { t } = useTranslation();
    let receivedPoAs = 0;
    if ("receivedPowerOfAttorneys" in data) {
      receivedPoAs = data.receivedPowerOfAttorneys.length;
    }
    const onSelectTab = (tab: NoticeActiveView) =>
      history.push(
        meetingUrl(data, tab, { isAdminPreview }),
        meetingUrlState(isAdminPreview, history),
      );

    const tabs = createTabs({
      isShareholder,
      isRecipient,
      givenPowerOfAttorneyTo,
      activeView,
      onSelectTab,
      receivedPoAs,
      t,
      attendanceData: prepareAttendanceData(data),
      meetingStatus: data.electronicGeneralMeeting?.status,
      hasMinutes: data.minutesDocumentContent !== undefined,
      conductElectronicGeneralMeeting:
        "conductElectronicGeneralMeeting" in data && data["conductElectronicGeneralMeeting"],
    });
    const meetingStatus = when(
      "electronicGeneralMeeting" in data && data.electronicGeneralMeeting?.status,
      getMeetingStatusTag,
    );
    const [messageVisible, setMessageVisible] = useState(isAdminPreview);

    return (
      <>
        <VBox bg gap={false}>
          {messageVisible && (
            <Alert
              message={
                <div>
                  <strong>{t("meetings.general-meeting.admin-preview.important")}</strong>
                  <span>{t("meetings.general-meeting.admin-preview.info")}</span>
                  <LinkButton
                    label={t("meetings.general-meeting.admin-preview.hide-message")}
                    onClick={() => setMessageVisible(!messageVisible)}
                  />
                </div>
              }
              type={"info"}
            />
          )}
          <Box
            paddingLeft={"md"}
            paddingTop={"sm"}
            padding
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <LogoWithCompanyName
              companyName={data.companyName}
              orgNo={data.orgNo}
              backUrl={backUrl}
            />
            <ShareholderUserMenu appBar={appBar} data={data} />
          </Box>
          <AlertContainer messageStore={alertStore} offset={"above"} />
          <VBox container paddingX maxWidth="xl">
            <HeaderMeetingInfo
              title={t("dashboard.general")}
              companyName={data.companyName}
              meetingStartDate={data.meetingStartDate}
              orgNo={data.orgNo}
              edit={edit}
              meetingStatus={meetingStatus}
              meetingLink={data.meetingLink}
              actions={
                givePowerOfAttorney
                  ? [
                      {
                        key: "poa",
                        variant: "secondary",
                        label: { key: "noticeForShareholder.givePoAButton" },
                        onClick: givePowerOfAttorney,
                      },
                    ]
                  : undefined
              }
            />
          </VBox>
        </VBox>

        <Box sticky={"top"} display={"block"} className={"z-10"} bg borderBottom>
          <Tabs tabs={tabs} container={!isMobileView()} maxWidth="xl" />
        </Box>
      </>
    );
  },
);

const BodyShareholder: FC<{
  data: GetNoticeForShareholderResponse;
  selectedAttendance: OptionFieldModel<AttendanceOption>;
  onDownloadAttachment: (id: string) => void;
  onRegisterAttendance?: () => void;
  onResetAttendance?: () => void;
  onVoteForMatter?: (votingContext: VotingContext, vote: IndividualVote) => void;
  onResetVote?: (votingContext: VotingContext) => void;
  agendaItemActionResult?: Result<AlertMessage, AlertMessage>;
  activeView: NoticeActiveView;
  poaModel: GivePowerOfAttorneyModel;
  agendaItem?: AgendaItemType;
  openAgendaItem: (agendaItem?: AgendaItemType) => void;
  previewAttachmentState?: PreviewState;
  onCloseAttachmentPreview: () => void;
  messageStore: AlertMessageStore;
  downloadNotice: DownloadAsPdfButtonProps;
  attendanceData: AttendanceData;
}> = observer(
  ({
    data,
    selectedAttendance,
    onDownloadAttachment,
    onRegisterAttendance,
    onResetAttendance,
    onVoteForMatter,
    onResetVote,
    agendaItemActionResult,
    activeView,
    poaModel,
    agendaItem,
    openAgendaItem,
    previewAttachmentState,
    onCloseAttachmentPreview,
    messageStore,
    downloadNotice,
    attendanceData,
  }) => {
    const { t } = useTranslation();
    const { formatPercent } = useLocaleFormat();
    const lang = useLanguage();

    const render = () => {
      switch (activeView) {
        case NoticeActiveView.notice:
          return (
            <VBox container maxWidth={"xl"} gap={"md"} paddingTop={"md"}>
              {data.electronicGeneralMeeting?.status === GeneralMeetingStatus.STARTED && (
                <Alert
                  icon={"arrows-clockwise"}
                  type={"info"}
                  message={{
                    key: "general-meeting.started-attend-info",
                    params: {
                      url: meetingUrl(data, NoticeActiveView.meeting, {
                        isAdminPreview: poaModel.isAdminPreview,
                      }),
                    },
                  }}
                />
              )}
              <Box gap={"md"} vertical={isMobileView()}>
                <MeetingDocumentView
                  content={data.noticeDocumentContent}
                  uploadedAttachments={data.attachments}
                />
                <VBox gap={"md"} className="max-w-[340px]">
                  <NoticeActionsView
                    download={downloadNotice}
                    addToCalendar={{
                      ...data,
                      title: t("dashboard.general"),
                    }}
                  />
                  <AttachmentsCard
                    attachments={data.attachments}
                    onDownload={onDownloadAttachment}
                  />
                  <Diagram
                    diagramData={createDiagramDataForNotice(
                      data.ownership,
                      data.noticeSentDate,
                      data.totalNumberOfVotes,
                      data.receivedPowerOfAttorneys,
                      formatPercent,
                      lang,
                    )}
                  />
                  <VotingEligibilityInfo data={data} />
                </VBox>
              </Box>
            </VBox>
          );

        case NoticeActiveView.poa:
          return (
            <Box vertical={isMobileView()} container maxWidth={"lg"} gap={"md"} paddingTop>
              <GivePowerOfAttorneyView
                model={poaModel}
                ownership={data.ownership}
                companyName={data.companyName}
                date={data.meetingStartDate}
                receivedPowerOfAttorneys={data.receivedPowerOfAttorneys}
                attendanceData={attendanceData}
                meetingStatus={data.electronicGeneralMeeting?.status}
                messageStore={messageStore}
                shareTypes={data.shareTypes}
              />
              <VBox gap={"md"}>
                <Diagram
                  diagramData={createDiagramDataForNotice(
                    data.ownership,
                    data.noticeSentDate,
                    data.totalNumberOfVotes,
                    [],
                    formatPercent,
                    lang,
                    { key: "share-of-own-votes" },
                  )}
                />
                <VotingEligibilityInfo data={data} />
              </VBox>
            </Box>
          );

        case NoticeActiveView.receivedPoA:
          return (
            <Box container vertical={isMobileView()} maxWidth={"xl"} gap={"md"} paddingTop={"md"}>
              <YourPowerOfAttorneyView
                receivedPowerOfAttorneys={data.receivedPowerOfAttorneys}
                totalNumberOfVotes={data.totalNumberOfVotes}
              />
              <VBox gap={"md"}>
                <Diagram
                  diagramData={createDiagramDataForNotice(
                    data.receivedPowerOfAttorneys.flatMap((e) => e.ownership),
                    data.noticeSentDate,
                    data.totalNumberOfVotes,
                    [],
                    formatPercent,
                    lang,
                    { key: "share-of-votes-from-poas" },
                  )}
                />
                <VotingEligibilityInfo data={data} />
              </VBox>
            </Box>
          );

        case NoticeActiveView.minutes:
          return (
            <VBox container maxWidth={"lg"} gap={"md"} paddingTop={"md"}>
              <MeetingDocumentView
                content={data.minutesDocumentContent}
                uploadedAttachments={data.attachments}
              />
            </VBox>
          );

        case NoticeActiveView.meeting:
          return (
            <VBox container={!isMobileView()} maxWidth={"lg"} gap={"md"} paddingTop={"md"}>
              <GeneralMeeting
                data={data}
                selectedAttendance={selectedAttendance}
                onRegisterAttendance={onRegisterAttendance}
                onResetAttendance={onResetAttendance}
                onVoteForMatter={onVoteForMatter}
                onResetVote={onResetVote}
                onDownloadAttachment={onDownloadAttachment}
                agendaItemActionResult={agendaItemActionResult}
                agendaItem={agendaItem}
                openAgendaItem={openAgendaItem}
                poaModel={poaModel}
                messageStore={messageStore}
                isPreview={poaModel.isAdminPreview}
              />
            </VBox>
          );

        case NoticeActiveView.powerOfAttorneyRecipientView:
          return (
            <Box container vertical={isMobileView()} maxWidth={"lg"} gap={"md"} paddingTop={"md"}>
              <YourPowerOfAttorneyView
                receivedPowerOfAttorneys={data.receivedPowerOfAttorneys}
                totalNumberOfVotes={data.totalNumberOfVotes}
              />
              <VBox gap={"md"}>
                <Diagram
                  diagramData={createDiagramDataForNotice(
                    data.receivedPowerOfAttorneys.flatMap((e) => e.ownership),
                    data.noticeSentDate,
                    data.totalNumberOfVotes,
                    [],
                    formatPercent,
                    lang,
                    { key: "share-of-votes-from-poas" },
                  )}
                />
                <VotingEligibilityInfo data={data} />
              </VBox>
            </Box>
          );
      }
    };

    return (
      <>
        {render()}
        {previewAttachmentState?.type === "preview" && (
          <FilePreviewDialog
            filename={previewAttachmentState.filename}
            file={previewAttachmentState.file}
            onClose={onCloseAttachmentPreview}
          />
        )}
        <BottomSpacer />
      </>
    );
  },
);

const BodyPoARecipient: FC<{
  data: GetNoticeForPowerOfAttorneyRecipientResponse;
  selectedAttendance: OptionFieldModel<AttendanceOption>;
  onDownloadAttachment: (id: string) => void;
  onRegisterAttendance?: () => void;
  onResetAttendance?: () => void;
  onVoteForMatter?: (votingContext: VotingContext, vote: IndividualVote) => void;
  onResetVote?: (votingContext: VotingContext) => void;
  agendaItemActionResult?: Result<AlertMessage, AlertMessage>;
  activeView: NoticeActiveView;
  poaModel?: GivePowerOfAttorneyModel;
  agendaItem?: AgendaItemType;
  openAgendaItem: (agendaItem?: AgendaItemType) => void;
  messageStore: AlertMessageStore;
  previewState?: PreviewState;
  closePreview?: () => void;
  downloadNotice: DownloadAsPdfButtonProps;
}> = observer(
  ({
    data,
    selectedAttendance,
    onDownloadAttachment,
    onRegisterAttendance,
    onResetAttendance,
    onVoteForMatter,
    onResetVote,
    agendaItemActionResult,
    activeView,
    poaModel,
    agendaItem,
    openAgendaItem,
    messageStore,
    previewState,
    closePreview,
    downloadNotice,
  }) => {
    const { formatPercent } = useLocaleFormat();
    const lang = useLanguage();
    const { t } = useTranslation();

    const render = () => {
      switch (true) {
        case activeView === NoticeActiveView.notice:
          return (
            <Box gap={"md"} container maxWidth={"xl"} paddingTop vertical={isMobileView()}>
              {previewState?.type === "preview" && (
                <FilePreviewDialog
                  filename={previewState.filename}
                  file={previewState.file}
                  onClose={closePreview}
                />
              )}
              <MeetingDocumentView
                content={data.noticeDocumentContent}
                uploadedAttachments={data.attachments}
              />
              <VBox gap={"md"} className="max-w-[340px]">
                <NoticeActionsView
                  download={downloadNotice}
                  addToCalendar={{
                    ...data,
                    title: t("dashboard.general"),
                  }}
                />
                <AttachmentsCard attachments={data.attachments} onDownload={onDownloadAttachment} />
                <Diagram
                  diagramData={createDiagramDataForNotice(
                    data.ownNotices.flatMap((d) => d.ownership),
                    data.noticeSentDate,
                    data.totalNumberOfVotes,
                    data.receivedPowerOfAttorneys,
                    formatPercent,
                    lang,
                  )}
                />
                <VotingEligibilityInfo data={data} />
              </VBox>
            </Box>
          );

        case poaModel && activeView === NoticeActiveView.poa:
          return (
            <Box gap={"md"} container maxWidth={"xl"} paddingTop vertical={isMobileView()}>
              <GivePowerOfAttorneyView
                model={poaModel}
                ownership={data.ownNotices.flatMap((d) => d.ownership)}
                companyName={data.companyName}
                date={data.meetingStartDate}
                receivedPowerOfAttorneys={data.receivedPowerOfAttorneys}
                attendanceData={prepareAttendanceData(data)}
                meetingStatus={data.electronicGeneralMeeting?.status}
                messageStore={messageStore}
                shareTypes={data.shareTypes}
              />
              {data.ownNotices.length > 0 && (
                <VBox>
                  <Diagram
                    diagramData={createDiagramDataForNotice(
                      data.ownNotices.flatMap((d) => d.ownership),
                      data.noticeSentDate,
                      data.totalNumberOfVotes,
                      [],
                      formatPercent,
                      lang,
                      { key: "share-of-own-votes" },
                    )}
                  />
                </VBox>
              )}
            </Box>
          );

        case activeView === NoticeActiveView.receivedPoA:
          return (
            <Box gap={"md"} container maxWidth={"xl"} paddingTop vertical={isMobileView()}>
              <YourPowerOfAttorneyView
                receivedPowerOfAttorneys={data.receivedPowerOfAttorneys}
                totalNumberOfVotes={data.totalNumberOfVotes}
              />
              <VBox gap={"md"}>
                <Diagram
                  diagramData={createDiagramDataForNotice(
                    data.receivedPowerOfAttorneys.flatMap((e) => e.ownership),
                    data.noticeSentDate,
                    data.totalNumberOfVotes,
                    [],
                    formatPercent,
                    lang,
                    { key: "share-of-votes-from-poas" },
                  )}
                />
                <VotingEligibilityInfo data={data} />
              </VBox>
            </Box>
          );

        case activeView === NoticeActiveView.meeting:
          return (
            <VBox container={!isMobileView()} maxWidth={"lg"} gap={"md"} paddingTop={"md"}>
              <GeneralMeeting
                data={data}
                selectedAttendance={selectedAttendance}
                onRegisterAttendance={onRegisterAttendance}
                onResetAttendance={onResetAttendance}
                onVoteForMatter={onVoteForMatter}
                onResetVote={onResetVote}
                agendaItemActionResult={agendaItemActionResult}
                agendaItem={agendaItem}
                openAgendaItem={openAgendaItem}
                poaModel={poaModel}
                messageStore={messageStore}
                isPreview={poaModel?.isAdminPreview}
              />
            </VBox>
          );
      }
    };

    return (
      <>
        {render()}
        {isMobileView() && <BottomSpacer />}
      </>
    );
  },
);

const YourPowerOfAttorneyView: FC<{
  receivedPowerOfAttorneys: ReceivedPowerOfAttorney[];
  totalNumberOfVotes: number;
}> = observer(({ receivedPowerOfAttorneys, totalNumberOfVotes }) => {
  const { t } = useTranslation();
  const { formatNumber, formatPercent } = useLocaleFormat();
  const locale = useLanguage();
  const sumFromPoAs = votesForOwnership(receivedPowerOfAttorneys.flatMap((e) => e.ownership));
  return (
    <Card gap={"md"} grow>
      <h4>{t("noticeForShareholder.poa.received")}</h4>
      <Table withRowBorders variant="IN_CARD_DENSE">
        <thead>
          <tr>
            <th align="left">
              <Box maxWidth="sm">{t("transactions.shareholder")}</Box>
            </th>
            <th align="right">
              <Box maxWidth="sm" justifyContent={"end"}>
                {t("shareClasses.shareClassForm.numberOfVotes")}
              </Box>
            </th>
            <th align="right">{t("share-of-votes")}</th>
            <th align="right">{t("date-given")}</th>
          </tr>
        </thead>
        <tbody>
          {receivedPowerOfAttorneys.map((e) => (
            <tr key={e.name}>
              <td>{e.name}</td>
              <td align="right">{formatNumber(votesForOwnership(e.ownership))}</td>
              <td align="right">{formatPercent(relativeVotes(e.ownership, totalNumberOfVotes))}</td>
              <td align="right">{formatDate(locale, e.poaGivenAt)}</td>
            </tr>
          ))}
          {receivedPowerOfAttorneys.length > 1 && (
            <tr>
              <td>{t("total")}</td>
              <td align="right">{formatNumber(sumFromPoAs)}</td>
              <td align="right">{formatPercent(percentOf(sumFromPoAs, totalNumberOfVotes))}</td>
              <td />
            </tr>
          )}
        </tbody>
      </Table>
    </Card>
  );
});

const PageReload: FC<{ data: RequestPageReload }> = observer(({ data }) => {
  const { t } = useTranslation();
  return (
    <Dialog title={t(data.title)} submitLabel={t("ok")} onSubmit={() => window.location.reload()}>
      <Markdown value={t(data.reason)} enableLinks />
    </Dialog>
  );
});

export const NoticeForShareholderView: FC<{
  controller: NoticeForShareholderController;
  activeView?: NoticeActiveView;
  backUrl?: string;
  edit?: () => void;
  agendaItem?: AgendaItemType;
  openAgendaItem: (agendaItem?: AgendaItemType) => void;
}> = observer(({ controller, activeView, backUrl, edit, agendaItem, openAgendaItem }) => {
  const alertStore = useMessageStore();
  const dispatch = useDispatch();
  const header = (
    <Header
      data={controller.response}
      activeView={activeView || controller.activeView}
      givenPowerOfAttorneyTo={controller.givenPowerOfAttorneyTo}
      isRecipient={controller.hasReceivedPowerOfAttorney}
      backUrl={backUrl}
      edit={edit}
      isAdminPreview={controller.isAdminPreview}
      alertStore={alertStore}
      givePowerOfAttorney={
        canGivePowerOfAttorney({
          attendanceData: controller.attendanceData,
          givenPowerOfAttorneyTo: controller.givenPowerOfAttorneyTo,
          isShareholder: true,
          meetingStatus: controller.response.electronicGeneralMeeting?.status,
        })
          ? () =>
              dispatch([
                {
                  type: "navigate",
                  url: meetingUrl(controller.response, NoticeActiveView.poa, {
                    isAdminPreview: controller.isAdminPreview,
                  }),
                },
              ])
          : undefined
      }
    />
  );
  const body = (
    <>
      <BodyShareholder
        data={controller.response}
        selectedAttendance={controller.selectedAttendance}
        onDownloadAttachment={controller.openPreviewAttachmentDialog}
        onRegisterAttendance={controller.registerAttendance}
        onResetAttendance={controller.resetAttendance}
        onVoteForMatter={controller.onVoteForMatter}
        onResetVote={controller.resetVote}
        agendaItemActionResult={controller.agendaItemActionResult}
        activeView={activeView || controller.activeView}
        poaModel={controller}
        agendaItem={agendaItem}
        openAgendaItem={openAgendaItem}
        previewAttachmentState={controller.previewState}
        onCloseAttachmentPreview={controller.closePreviewAttachmentDialog}
        messageStore={alertStore}
        downloadNotice={controller.downloadNotice}
        attendanceData={controller.attendanceData}
      />
      {controller.requestPageReload && <PageReload data={controller.requestPageReload} />}
    </>
  );

  return (
    <>
      {header}
      {body}
    </>
  );
});

export const ExpiredPowerOfAttorneyView: FC<{
  companyName: string;
  orgNo: string;
}> = observer(({ companyName, orgNo }) => {
  const { t } = useTranslation();
  return (
    <FullScreenLayout
      appBar={
        <VBox borderBottom bg padding>
          <LogoWithCompanyName companyName={companyName} orgNo={orgNo} />
        </VBox>
      }
      centerPanel={
        <Box grow={"golden"} alignItems={"center"}>
          <EmptyState
            title={t("poa.link-expired-title")}
            description={t("poa.link-expired-description")}
            illustration={<Icon name={"lock-key"} size={"jumbo"} duotone />}
            actions={
              <Button
                variant={"secondary"}
                label={t("user-settings.take-me-to-the-login-page")}
                onClick={() => (window.location.href = "/")}
              />
            }
          />
        </Box>
      }
    />
  );
});
export const PowerOfAttorneyRecipientView: FC<{
  controller: PowerOfAttorneyRecipientController;
  activeView?: NoticeActiveView;
  agendaItem?: AgendaItemType;
  openAgendaItem: (agendaItem?: AgendaItemType) => void;
}> = observer(({ controller, activeView, agendaItem, openAgendaItem }) => {
  const alertStore = useMessageStore();
  const header = (
    <Header
      data={controller.response}
      activeView={activeView || controller.activeView}
      givenPowerOfAttorneyTo={controller.givenPowerOfAttorneyTo}
      isShareholder={controller.ownNoticeController !== undefined}
      alertStore={alertStore}
      isRecipient
    />
  );
  const body = (
    <BodyPoARecipient
      data={controller.response}
      selectedAttendance={controller.selectedAttendance}
      onDownloadAttachment={controller.downloadAttachment}
      onRegisterAttendance={controller.registerAttendance}
      onResetAttendance={controller.resetAttendance}
      onVoteForMatter={controller.onVoteForMatter}
      onResetVote={controller.resetVote}
      agendaItemActionResult={controller.agendaItemActionResult}
      activeView={activeView || controller.activeView}
      poaModel={controller.ownNoticeController}
      agendaItem={agendaItem}
      openAgendaItem={openAgendaItem}
      messageStore={alertStore}
      previewState={controller.previewState}
      closePreview={controller.closePreview}
      downloadNotice={controller.downloadNotice}
    />
  );
  return (
    <>
      {header}
      {body}
    </>
  );
});

const Diagram: FC<{ diagramData: DiagramData }> = observer(({ diagramData }) => {
  const { t } = useTranslation();
  return (
    <Card>
      <Box justifyContent="center">
        <div className="color-primary grow h-40">
          <AutoSizer>
            {({ width, height }) => (
              <PieChart data={diagramData.data} width={width} height={height} />
            )}
          </AutoSizer>
        </div>
      </Box>
      <VBox gap={false} padding={false}>
        <strong>
          <Box justifyContent={"space-between"}>
            {diagramData.title.map((title) => (
              <div key={t(title)}>{t(title)}</div>
            ))}
          </Box>
        </strong>
        <div className={"subtle"}>{t(diagramData.subtitle)}</div>
      </VBox>

      {diagramData.infoRows?.map((row) => (
        <Box key={row.map((r) => t(r)).join()} justifyContent={"space-between"}>
          {row.map((label) => (
            <div key={t(label)}>{t(label)}</div>
          ))}
        </Box>
      ))}
    </Card>
  );
});

export const HeaderRowInMeetingTab: FC<{
  isScreenShareMode: boolean;
  openInMeetings: () => void;
  openVideoMeeting?: () => void;
  toggleScreenShareMode: () => void;
}> = observer(({ isScreenShareMode, openInMeetings, openVideoMeeting, toggleScreenShareMode }) => {
  const { t } = useTranslation();
  return (
    <Box justifyContent={"space-between"}>
      <Box>
        {t("general-meeting.participant-view")}
        <InfoButton infoText={t("general-meeting.participant-view.info-text")} />
      </Box>
      <Box gap={"lg"}>
        <LinkButton
          icon={"monitor"}
          label={
            isScreenShareMode
              ? t("general-meeting.exit-screen-share-mode")
              : t("general-meeting.screen-share-mode")
          }
          onClick={toggleScreenShareMode}
        />
        <DropdownMenu
          button={{
            type: "link",
            label: t("options"),
          }}
          items={[
            {
              type: "item",
              label: t("meetings.open.meeting-portal"),
              onSelect: openInMeetings,
              icon: "arrow-square-out",
            },
            openVideoMeeting && {
              type: "item",
              label: t("meetings.open.video-meeting"),
              onSelect: openVideoMeeting,
              icon: "video-camera",
            },
          ]}
        />
      </Box>
    </Box>
  );
});

export const GeneralMeeting: FC<{
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
  selectedAttendance: OptionFieldModel<AttendanceOption>;
  onRegisterAttendance?: () => void;
  onResetAttendance?: () => void;
  onVoteForMatter?: (votingContext: VotingContext, vote: IndividualVote) => void;
  onResetVote?: (votingContext: VotingContext) => void;
  onDownloadAttachment?: (id: string) => void;
  agendaItemActionResult?: Result<AlertMessage, AlertMessage>;
  agendaItem?: AgendaItemType;
  openAgendaItem: (item?: AgendaItemType) => void;
  poaModel?: GivePowerOfAttorneyModel;
  messageStore?: AlertMessageStore;
  isPreview?: boolean;
}> = observer(
  ({
    data,
    selectedAttendance,
    onRegisterAttendance,
    onResetAttendance,
    agendaItemActionResult,
    onVoteForMatter,
    onResetVote,
    onDownloadAttachment,
    agendaItem,
    openAgendaItem,
    poaModel,
    messageStore,
    isPreview,
  }) => {
    const attendanceData = prepareAttendanceData(data);
    return (
      <>
        <GeneralMeetingMainPageWarning
          status={data.electronicGeneralMeeting?.status}
          data={attendanceData}
          poaModel={poaModel}
          isPreview={isPreview}
        />
        {agendaItem ? (
          <AgendaItemView
            agendaItemActionResult={agendaItemActionResult}
            messageStore={messageStore}
            data={data}
            selectedAttendance={selectedAttendance}
            agendaItem={agendaItem}
            onVoteForMatter={onVoteForMatter}
            onResetVote={onResetVote}
            onBack={() => openAgendaItem(undefined)}
            onNext={when(
              nextAgendaItem(getAgendaItems(data), agendaItem),
              (next) => () => openAgendaItem(next),
            )}
            onPrev={when(
              prevAgendaItem(getAgendaItems(data), agendaItem),
              (prev) => () => openAgendaItem(prev),
            )}
            onRegisterAttendance={onRegisterAttendance}
            onResetAttendance={onResetAttendance}
            onDownloadAttachment={onDownloadAttachment}
            isPowerOfAttorneyGiven={poaModel?.givenPowerOfAttorney === true}
          />
        ) : (
          <GeneralMeetingMainPage data={data} openAgendaItem={openAgendaItem} />
        )}
      </>
    );
  },
);

const GeneralMeetingMainPageWarning: FC<{
  status?: GeneralMeetingStatus;
  data: AttendanceData;
  poaModel?: GivePowerOfAttorneyModel;
  isPreview?: boolean;
}> = observer(({ status, data, poaModel, isPreview }) => {
  const { t } = useTranslation();
  const { isPresent, isFinished } = data;

  if (isPreview) return null;
  else if (!poaModel?.givenPowerOfAttorney && status === GeneralMeetingStatus.PUBLISHED) {
    return <Alert type={"info"} icon={"info"} message={t("general-meeting.not-started-info")} />;
  } else if (!poaModel?.givenPowerOfAttorney && !isPresent && isFinished) {
    return <Alert type={"warning"} message={{ key: "general-meeting.not-attending-warning" }} />;
  } else if (poaModel?.givenPowerOfAttorney === true) {
    if (isPresent) {
      return (
        <Alert
          type={"success"}
          icon={"check"}
          message={{
            key: "general-meeting.poa-attended-info",
            params: {
              poaRecipientName: poaModel.poaRecipientName,
            },
          }}
        />
      );
    } else {
      return (
        <Alert
          type={isFinished ? "warning" : "info"}
          icon={isFinished ? false : "info"}
          message={{
            key: isFinished
              ? "general-meeting.poa-not-attended-warning"
              : "general-meeting.poa-not-attended-info",
            params: {
              poaRecipientName: poaModel.poaRecipientName,
            },
          }}
        />
      );
    }
  } else return null;
});

const GeneralMeetingMainPage: FC<{
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
  openAgendaItem: (item: AgendaItemType) => void;
}> = observer(({ data, openAgendaItem }) => {
  const { t } = useTranslation();
  const language = useLanguage();

  return (
    <VBox gap={"md"}>
      {getAgendaItems(data).map((agendaItem) => {
        const onOpen = () => openAgendaItem(agendaItem);
        switch (agendaItem.type) {
          case "attendance":
            return (
              <AttendanceSummary
                key={agendaItem.type}
                data={prepareAttendanceData(data)}
                onOpen={onOpen}
              />
            );
          case "meetingChair":
            return (
              <MatterForVotingSummary
                key={agendaItem.type}
                data={prepareMeetingChairVotingData({ data, t, language })}
                onOpen={onOpen}
              />
            );
          case "approveAgenda":
            return (
              <MatterForVotingSummary
                key={agendaItem.type}
                data={prepareApproveAgendaVotingData({ data, t, language })}
                onOpen={onOpen}
              />
            );
          case "matter":
            return (
              <MatterForVotingSummary
                key={agendaItem.matter.decision.decisionId}
                data={prepareMatterForVotingData({ data, matter: agendaItem.matter })}
                onOpen={onOpen}
              />
            );
        }
      })}
    </VBox>
  );
});

const AgendaItemView: FC<{
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
  selectedAttendance: OptionFieldModel<AttendanceOption>;
  agendaItem?: AgendaItemType;
  onBack: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  onVoteForMatter?: (votingContext: VotingContext, vote: IndividualVote) => void;
  onResetVote?: (votingContext: VotingContext) => void;
  onRegisterAttendance?: () => void;
  onDownloadAttachment?: (id: string) => void;
  onResetAttendance?: () => void;
  agendaItemActionResult?: Result<AlertMessage, AlertMessage>;
  messageStore?: AlertMessageStore;
  isPowerOfAttorneyGiven?: boolean;
}> = observer(({ data, agendaItem, isPowerOfAttorneyGiven, ...props }) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const attendanceData = prepareAttendanceData(data);

  switch (agendaItem?.type) {
    case "attendance":
      return (
        <AttendanceView
          {...props}
          data={attendanceData}
          isDisabled={isPowerOfAttorneyGiven || hasVoted(data)}
        />
      );
    case "meetingChair":
      return (
        <MatterForVotingView
          {...props}
          data={prepareMeetingChairVotingData({ data, t, language })}
          isDisabled={isPowerOfAttorneyGiven || !attendanceData.isPresent}
          isPresent={attendanceData.isPresent}
        />
      );
    case "approveAgenda":
      return (
        <MatterForVotingView
          {...props}
          data={prepareApproveAgendaVotingData({ data, t, language })}
          isDisabled={isPowerOfAttorneyGiven || !attendanceData.isPresent}
          isPresent={attendanceData.isPresent}
        />
      );
    case "matter":
      return (
        <MatterForVotingView
          {...props}
          data={prepareMatterForVotingData({ data, matter: agendaItem.matter })}
          isDisabled={isPowerOfAttorneyGiven || !attendanceData.isPresent}
          isPresent={attendanceData.isPresent}
        />
      );
    default:
      return null;
  }
});

function AgendaItemDetailsCard({
  fullscreen,
  onBack,
  onNext,
  onPrev,
  children,
  header,
  footer,
  ...props
}: CardProps & {
  fullscreen?: boolean;
  onBack?: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  header?: ReactElement;
  footer?: ReactElement;
}) {
  const { t } = useTranslation();
  const navigation = (
    <Box grow justifyContent={"space-between"} className={"self-stretch"}>
      {onBack && <LinkButton label={t("back")} arrow={"left"} onClick={onBack} />}
      <Box>
        <IconButton
          icon={"caret-down"}
          tooltip={t("next")}
          color={"inherit"}
          onClick={onNext}
          disabled={!onNext}
        />
        <IconButton
          icon={"caret-up"}
          tooltip={t("previous")}
          color={"inherit"}
          onClick={onPrev}
          disabled={!onPrev}
        />
        {onBack && (
          <IconButton icon={"x"} tooltip={t("close")} color={"inherit"} onClick={onBack} />
        )}
      </Box>
    </Box>
  );
  return fullscreen ? (
    <FullScreenLayout
      /* Note: z-index above the sticky tabs on mobile */
      className={"z-20"}
      pageHeader={
        <VBox bg padding={"lg"} gap={"lg"} alignItems={"start"}>
          {navigation}
          {header}
        </VBox>
      }
      centerPanel={
        <VBox bg grow paddingX={"lg"} gap={"lg"} alignItems={"start"}>
          {children}
        </VBox>
      }
      footerPanel={
        footer && (
          <VBox
            bg
            padding={"lg"}
            borderTop
            htmlProps={{
              style: {
                boxShadow: "0 -4px 15px -3px rgb(0 0 0 / 0.1), 0 0px 6px -4px rgb(0 0 0 / 0.1)",
              },
            }}
          >
            {footer}
          </VBox>
        )
      }
    />
  ) : (
    <Card padding={"lg"} alignItems={"start"} gap={"lg"} {...props}>
      {navigation}
      {header}
      {children}
      {footer}
    </Card>
  );
}

const attendanceHeader = (data: AttendanceData): Omit<AgendaItemCardProps, "openAction"> => ({
  id: "attendance",
  status: data.isStarted
    ? {
        label: { key: "general-meeting.attendance.self-registration-opened" },
        color: "info",
      }
    : data.isFinished
      ? {
          label: { key: "done" },
          color: "success",
        }
      : {
          label: { key: "general-meeting.attendance.self-registration-not-opened" },
          color: "neutral",
        },
  subtitleText:
    data.isStarted || data.isFinished
      ? data.isPresent
        ? { key: "general-meeting.your-attendance-has-been-registered" }
        : { key: "general-meeting.your-attendance-has-not-been-registered" }
      : { key: "general-meeting.register-attendance-not-started-description" },
  title: { key: "general-meeting.register" },
});

export const AttendanceSummary = observer(function AttendanceSummary({
  data,
  onOpen,
}: {
  data: AttendanceData;
  onOpen: () => void;
}) {
  return (
    <AgendaItemCard
      onOpen={onOpen}
      openVariant={data.isStarted && !data.isPresent ? "primary" : undefined}
      openLabel={
        data.isStarted && !data.isPresent
          ? { key: "general-meeting.register-your-attendance" }
          : undefined
      }
      {...attendanceHeader(data)}
    />
  );
});

export const AttendanceView: FC<{
  data: AttendanceData;
  selectedAttendance: OptionFieldModel<AttendanceOption>;
  onRegisterAttendance?: () => void;
  onResetAttendance?: () => void;
  agendaItemActionResult?: Result<AlertMessage, AlertMessage>;
  onBack: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  isDisabled?: boolean;
}> = observer(
  ({
    data,
    selectedAttendance,
    onBack,
    onNext,
    onPrev,
    onRegisterAttendance,
    onResetAttendance,
    agendaItemActionResult,
    isDisabled,
  }) => {
    const { formatNumber, formatPercent } = useLocaleFormat();
    const { t } = useTranslation();
    const { totalNumberOfVotes, receivedPowerOfAttorneys, isStarted, isPresent } = data;
    const numberOfRegistrations = selectedAttendance.selected.length;

    const toggleButtonLabel = isPresent
      ? t("you-are-attending")
      : t("confirm-attendances", {
          numberOfRegistrations: numberOfRegistrations,
        });

    const columns: TableColumn<AttendanceOption>[] = compact([
      isPresent && {
        name: "presentIcon",
        label: "",
        align: "left",
        width: "1%",
        notSorted: true,
        getValue: (attending) => (
          <Box alignItems={"center"}>
            {selectedAttendance.isSelected(attending) ? (
              <Icon name={"check-circle"} color={"success"} weight={"fill"} />
            ) : (
              <Icon name={"x-circle"} />
            )}
          </Box>
        ),
      },
      !isPresent && {
        name: "selected",
        label: "",
        align: "left",
        width: "1%",
        notSorted: true,
        getValue: (attending) => (
          <Checkbox onChange={noop} checked={selectedAttendance.isSelected(attending)} />
        ),
      },
      {
        name: "shareholder",
        label: t("transactions.shareholder"),
        align: "left",
        notSorted: true,
        getValue: (data) => (
          <div>
            {t(data.label)}
            <br />
            <small className={"subtle"}>{t(data.subtitle)}</small>
          </div>
        ),
      },
      {
        name: "numberOfVotes",
        label: t("shareClasses.shareClassForm.numberOfVotes"),
        align: "right",
        notSorted: true,
        getValue: (data) => formatNumber(votesForOwnership(data.ownership)),
      },
      {
        name: "share-of-votes",
        label: t("share-of-votes"),
        align: "right",
        notSorted: true,
        getValue: (data) => formatPercent(relativeVotes(data.ownership, totalNumberOfVotes)),
      },
    ]);
    return (
      <AgendaItemDetailsCard
        fullscreen={isMobileView()}
        onBack={onBack}
        onNext={onNext}
        onPrev={onPrev}
        header={<AgendaItemHeader {...attendanceHeader(data)} />}
        footer={
          <Box gap={"md"}>
            <ToggleButton
              selected={isPresent}
              disabled={isDisabled || !isStarted || numberOfRegistrations === 0}
              label={toggleButtonLabel}
              loading={isPending(agendaItemActionResult)}
              onClick={() => isStarted && onRegisterAttendance?.()}
              icon={isPresent ? "check-circle" : undefined}
              disableMinWidth={isMobileView()}
              grow
            />
            {isStarted && isPresent && (
              <ToggleButton
                icon={"arrow-counter-clockwise"}
                tooltip={t("reset")}
                loading={isPending(agendaItemActionResult)}
                onClick={onResetAttendance}
                disabled={isDisabled}
                disableMinWidth
              />
            )}
          </Box>
        }
      >
        {!isPresent &&
          selectedAttendance.options.some((attendance) => attendance.isAdditionalNotice) && (
            <Alert
              message={{
                key: "general-meeting.attendance.otherAttendance.info",
                params: {
                  name:
                    selectedAttendance.options.length > 0
                      ? selectedAttendance.options[0].label
                      : "",
                },
              }}
              type={"info"}
            />
          )}
        <VBox gap={"lg"}>
          <b>{t("your-representation")}</b>
          <DataTable
            variant={"IN_CARD"}
            withRowBorders={false}
            hoverable
            tableColumns={columns}
            tableData={selectedAttendance.options}
            onRowClick={(attending) =>
              isPresent ? undefined : selectedAttendance.toggle(attending)
            }
            getId={(row) => row.noticeId}
          />
          {receivedPowerOfAttorneys.length === 0 && (
            <div>{t("no-power-of-attorney-registered")}</div>
          )}
        </VBox>
      </AgendaItemDetailsCard>
    );
  },
);

const matterForVotingHeader = (
  data: MatterForVotingData,
  isInformationMatter?: boolean,
): Omit<AgendaItemCardProps, "openAction"> => {
  const isStarted = data.status === GeneralMeetingStatus.STARTED;
  const isFinished = data.status === GeneralMeetingStatus.FINISHED;
  const votingStarted = isStarted || isFinished;
  const votedFor = votingStarted && data.votingContext.votes.some((vote) => vote.vote === "for");
  const votedAgainst =
    votingStarted && data.votingContext.votes.some((vote) => vote.vote === "against");
  const votedBlank =
    votingStarted && data.votingContext.votes.some((vote) => vote.vote === "blank");
  return {
    id: data.matter.decision.decisionId,
    title: getDecisionActionLabel(
      data.matter.decision.decisionData.type,
      data.matter.decision.decisionData,
    ),
    subtitleText: isInformationMatter
      ? { key: "meetings.decision.information_matter" }
      : !votingStarted
        ? { key: "general-meeting.voting-not-started-description" }
        : votedFor
          ? { key: "general-meeting.you-voted-for" }
          : votedAgainst
            ? { key: "general-meeting.you-voted-against" }
            : votedBlank
              ? { key: "general-meeting.you-voted-blank" }
              : { key: "general-meeting.you-have-not-voted" },
    subtitleIcon: votedFor ? "thumbs-up" : votedAgainst ? "thumbs-down" : undefined,
    status: isStarted
      ? { label: { key: "opened" }, color: "info" }
      : isFinished
        ? { label: { key: "done" }, color: "success" }
        : { label: { key: "not-started" }, color: "neutral" },
  };
};

export const MatterForVotingSummary = observer(function MatterForVotingSummary({
  data,
  onOpen,
}: {
  data: MatterForVotingData;
  onOpen: () => void;
}) {
  const isStarted = data.status === GeneralMeetingStatus.STARTED;
  const hasVoted = data.votingContext.votes.length > 0;
  const isInformationMatter =
    !isProceduralMatter(data.votingContext) &&
    isInformationMatterInDocument(data.matter.decision.decisionData, DocumentType.general_meeting);

  return (
    <AgendaItemCard
      {...matterForVotingHeader(data, isInformationMatter)}
      onOpen={onOpen}
      openVariant={isStarted && !hasVoted ? "primary" : undefined}
      openLabel={
        isStarted && !isInformationMatter
          ? { key: "general-meeting.register-your-vote" }
          : undefined
      }
    />
  );
});

export const MatterForVotingView: FC<{
  agendaItemActionResult?: Result<AlertMessage, AlertMessage>;
  data: MatterForVotingData;
  onBack: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  onVoteForMatter?: (votingContext: VotingContext, vote: IndividualVote) => void;
  onResetVote?: (votingContext: VotingContext) => void;
  onDownloadAttachment?: (id: string) => void;
  isDisabled?: boolean;
  isPresent: boolean;
}> = observer(
  ({
    data,
    onBack,
    onNext,
    onPrev,
    onVoteForMatter,
    onResetVote,
    onDownloadAttachment,
    agendaItemActionResult,
    isDisabled,
    isPresent,
  }) => {
    const { t } = useTranslation();
    const isStarted = data.status === GeneralMeetingStatus.STARTED;
    const { matter, votingContext } = data;
    const isInformationMatter =
      !isProceduralMatter(votingContext) &&
      isInformationMatterInDocument(matter.decision.decisionData, DocumentType.general_meeting);
    return (
      <AgendaItemDetailsCard
        fullscreen={isMobileView()}
        onBack={onBack}
        onNext={onNext}
        onPrev={onPrev}
        header={<AgendaItemHeader {...matterForVotingHeader(data, isInformationMatter)} />}
        footer={
          isInformationMatter ? undefined : (
            <VBox gap={"md"}>
              {!isPresent && (
                <Alert
                  type={"warning"}
                  message={t("general-meeting.warning.register-attending-before-voting")}
                />
              )}
              <VotingButtons
                votes={votingContext.votes}
                onVoteForMatter={onVoteForMatter}
                onResetVote={onResetVote}
                votingContext={votingContext}
                agendaItemActionResult={agendaItemActionResult}
                isDisabled={isDisabled || !isStarted}
              />
            </VBox>
          )
        }
      >
        <VBox>
          {matter.decision.decisionData.type !== DecisionType.freetext && (
            <MatterExtraText
              texts={matter.decision.decisionIntroTexts}
              documentType={DocumentType.notice_general_meeting}
              isMultiLanguage={matter.isMultiLanguage}
            />
          )}
          <MatterText
            matterText={matter.textForReview}
            isMultiLanguage={matter.isMultiLanguage}
            isInformationMatter={isInformationMatter}
          />
          {matter.decision.decisionData.type !== DecisionType.freetext && (
            <MatterExtraText
              texts={matter.decision.decisionTexts}
              documentType={DocumentType.notice_general_meeting}
              isMultiLanguage={matter.isMultiLanguage}
            />
          )}
        </VBox>
        <Attachments
          attachments={matter.attachments}
          onDownloadAttachment={onDownloadAttachment}
          caption={{ key: "attachments-on-matter" }}
        />
      </AgendaItemDetailsCard>
    );
  },
);

const VotingButtons = observer(
  ({
    votes,
    onVoteForMatter,
    onResetVote,
    votingContext,
    agendaItemActionResult,
    isDisabled,
  }: {
    votes: GeneralMeetingIndividualVote[] | undefined;
    votingContext: VotingContext;
    onVoteForMatter?: (votingContext: VotingContext, vote: IndividualVote) => void;
    onResetVote?: (votingContext: VotingContext) => void;
    agendaItemActionResult?: Result<AlertMessage, AlertMessage>;
    isDisabled?: boolean;
  }) => {
    const { t } = useTranslation();
    const votedFor = votes?.some((vote) => vote.vote === "for");
    const votedAgainst = votes?.some((vote) => vote.vote === "against");
    const hasVoted = votedFor || votedAgainst;
    return (
      <Box gap={"md"}>
        <ToggleButton
          icon={"thumbs-up"}
          label={
            votedAgainst && isMobileView()
              ? undefined
              : votedFor
                ? t("general-meeting.you-voted-for")
                : t("general-meeting.vote-for")
          }
          onClick={() => onVoteForMatter?.(votingContext, "for")}
          selected={votedFor}
          disabled={isDisabled}
          loading={isPending(agendaItemActionResult)}
          disableMinWidth={isMobileView()}
          grow={isMobileView() && !votedAgainst}
        />
        <ToggleButton
          icon={"thumbs-down"}
          label={
            votedFor && isMobileView()
              ? undefined
              : votedAgainst
                ? t("general-meeting.you-voted-against")
                : t("general-meeting.vote-against")
          }
          onClick={() => onVoteForMatter?.(votingContext, "against")}
          selected={votedAgainst}
          disabled={isDisabled}
          loading={isPending(agendaItemActionResult)}
          disableMinWidth={isMobileView()}
          grow={isMobileView() && !votedFor}
        />
        {onResetVote && hasVoted && (
          <Button
            variant={"secondary"}
            icon={"arrow-counter-clockwise"}
            tooltip={t("reset")}
            loading={isPending(agendaItemActionResult)}
            onClick={() => onResetVote(votingContext)}
            disabled={isDisabled}
          />
        )}
      </Box>
    );
  },
);

const votingEligibilityTitleKeys: { [key in VotingEligibilityOfShares]?: TMessage } = {
  [VotingEligibilityOfShares.ALL]: undefined,
  [VotingEligibilityOfShares.SOME]: {
    key: "noticeForShareholder.votingEligibility.some.title",
  },
  [VotingEligibilityOfShares.NONE]: {
    key: "noticeForShareholder.votingEligibility.none.title",
  },
};

const VotingEligibilityInfo = observer(
  ({
    data,
  }: {
    data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
  }) => {
    const { t } = useTranslation();
    const warningTitle = votingEligibilityTitleKeys[data.votingEligibilityOfShares];
    return warningTitle ? (
      <Card bg={"alt2"}>
        <VBox justifyContent="center" maxWidth={"sm"}>
          <h6>{t(warningTitle)}</h6>
          {t("noticeForShareholder.votingEligibility.some-or-none.description")}
        </VBox>
      </Card>
    ) : null;
  },
);

export function ToMeetingTabMessage({
  message,
  agenda,
  openAgendaItem,
  linkText,
}: {
  message: TMessage;
  agenda?: AgendaItemType;
  openAgendaItem: (agenda?: AgendaItemType) => void;
  linkText?: TMessage;
}) {
  const { t } = useTranslation();

  return (
    <VBox alignItems={"start"}>
      {t(message)}{" "}
      {linkText && (
        <LinkButton
          label={t(linkText)}
          onClick={() => openAgendaItem(agenda)}
          underline
          weight={"semibold"}
        />
      )}
    </VBox>
  );
}
